import { useMemo, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import {
	Badge,
	Button,
	EmptyState,
	IllustrationEmpty,
	Spinner,
	Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@rentflowdev/ui'
import { useListAccessEventsQuery, useListSubAccountsQuery } from '@/api'
import type { AccessEventEntry } from '@/types'
import { DEFAULT_LOCALE } from '@/types'
import { useMemberRoles } from '@/lib/use-member-roles'

export const Route = createFileRoute('/_app/settings/access-log')({
	component: SettingsAccessLogPage,
})

const KIND_VARIANT = {
	DENIED: 'destructive',
	ROLE_CHANGED: 'secondary',
	HANDED_OVER: 'outline',
} as const

const formatAt = (iso: string) =>
	new Date(iso).toLocaleString(DEFAULT_LOCALE, {
		day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
	})

/**
 * The organization's access journal (RENTF-88): who was refused what, whose
 * role changed, which row went to whom. For those who run the team; the
 * gateway refuses everyone else.
 */
function SettingsAccessLogPage() {
	const { t } = useTranslation()
	const [before, setBefore] = useState<string | undefined>()
	const { data: events = [], isLoading, isFetching } = useListAccessEventsQuery(before ? { before } : undefined)
	const { data: members = [] } = useListSubAccountsQuery()
	const roles = useMemberRoles()

	const memberName = useMemo(() => {
		const names = new Map(members.map((m) => [m.id, m.fullName || m.email || m.phone || m.id]))
		return (id: unknown) => (typeof id === 'string' ? (names.get(id) ?? id) : '')
	}, [members])
	const roleName = (key: unknown) =>
		typeof key === 'string' ? (roles.find((r) => r.key === key)?.name ?? key) : ''
	const resourceName = (resource: string | null) =>
		resource ? t(`admin.roles.resources.${resource}`, { defaultValue: resource }) : ''

	// The names the server resolved, else the team's, else the id.
	const nameOf = (event: AccessEventEntry, id: unknown) => {
		const names = (event.details.names ?? {}) as Record<string, string>
		return typeof id === 'string' ? (names[id] ?? memberName(id)) : ''
	}

	const describe = (event: AccessEventEntry): string => {
		const d = event.details
		if (event.kind === 'DENIED') {
			if (!event.action) return t('settings.accessLog.deniedMessage', { message: String(d.message ?? '') })
			return t('settings.accessLog.denied', {
				action: t(`settings.accessLog.actions.${event.action}`, { defaultValue: event.action }),
				resource: resourceName(event.resource),
			})
		}
		if (event.kind === 'HANDED_OVER') {
			const member = typeof d.member === 'string' && d.member ? d.member : ''
			if (d.assigned) return t('settings.accessLog.propertyAssigned', { member: member || nameOf(event, (d.to as string[])[0]) })
			if (d.unassigned) return t('settings.accessLog.propertyUnassigned', { member: member || nameOf(event, (d.from as string[])[0]) })
			const to = Array.isArray(d.to) ? d.to.map((id) => nameOf(event, id)).filter(Boolean).join(', ') : ''
			return t('settings.accessLog.handedOver', { resource: resourceName(event.resource), to })
		}
		if (event.resource === 'Role') {
			return t(`settings.accessLog.role.${event.action}`, { role: event.targetId ?? '' })
		}
		const member = (typeof d.member === 'string' && d.member) || nameOf(event, event.targetId)
		if (d.invited) return t('settings.accessLog.member.invited', { member, role: roleName(d.to) })
		if (d.removed) return t('settings.accessLog.member.removed', { member, role: roleName(d.from) })
		const parts = [
			d.to ? t('settings.accessLog.member.role', { from: roleName(d.from), to: roleName(d.to) }) : null,
			d.statusTo ? t('settings.accessLog.member.status', {
				from: t(`settings.accessLog.statuses.${String(d.statusFrom)}`, { defaultValue: String(d.statusFrom) }),
				to: t(`settings.accessLog.statuses.${String(d.statusTo)}`, { defaultValue: String(d.statusTo) }),
			}) : null,
		].filter(Boolean)
		return `${member}: ${parts.join(', ')}`
	}

	if (isLoading) {
		return (
			<div className="flex h-full items-center justify-center">
				<Spinner />
			</div>
		)
	}

	if (events.length === 0 && !before) {
		return (
			<div className="flex h-full items-center justify-center p-6">
				<EmptyState
					illustration={<IllustrationEmpty />}
					title={t('settings.accessLog.emptyTitle')}
					description={t('settings.accessLog.emptyDescription')}
				/>
			</div>
		)
	}

	return (
		<div className="flex h-full flex-col gap-4 overflow-hidden px-4 py-4">
			<p className="text-sm text-(--color-muted-foreground)">{t('settings.accessLog.hint')}</p>
			<div className="flex-1 overflow-auto">
				<Table>
					<TableHeader>
						<TableRow>
							<TableHead className="w-40">{t('settings.accessLog.columns.at')}</TableHead>
							<TableHead className="w-48">{t('settings.accessLog.columns.actor')}</TableHead>
							<TableHead className="w-36">{t('settings.accessLog.columns.kind')}</TableHead>
							<TableHead>{t('settings.accessLog.columns.what')}</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{events.map((event) => (
							<TableRow key={event.id}>
								<TableCell className="whitespace-nowrap">{formatAt(event.at)}</TableCell>
								<TableCell>{event.actorName ?? nameOf(event, event.actorId)}</TableCell>
								<TableCell>
									<Badge variant={KIND_VARIANT[event.kind]}>{t(`settings.accessLog.kinds.${event.kind}`)}</Badge>
								</TableCell>
								<TableCell>
									<div>{describe(event)}</div>
									{typeof event.details.route === 'string' && (
										<div className="text-xs text-(--color-muted-foreground)">{event.details.route}</div>
									)}
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</div>
			<div className="flex gap-2">
				{before && (
					<Button variant="ghost" size="sm" onClick={() => setBefore(undefined)}>
						{t('settings.accessLog.newest')}
					</Button>
				)}
				{events.length >= 100 && (
					<Button variant="secondary" size="sm" disabled={isFetching} onClick={() => setBefore(events[events.length - 1]?.id)}>
						{t('settings.accessLog.older')}
					</Button>
				)}
			</div>
		</div>
	)
}
